import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchField from '@/component-library/widgets/SearchField/SearchField';
import { useSingleSendsEnabled } from '@/hooks/useSingleSendsEnabled';
import { CustomCard } from '@/modules/Dashboard/DashboardPage/Components/MetricComponents';

export interface Segment {
  id: string;
  segment: string;
  segmentType?: string;
  entityType?: string;
  tenantId: string;
  name: string;
  description: string;
  createdBy: string;
  createdAt: string;
  lastModifiedAt: string;
  peopleCount: string;
  usage: {
    singleSends: string[] | undefined;
    campaigns: string[] | undefined;
    dashboard: string[] | undefined;
    sqlSyncs: string[] | undefined;
    apiTriggered: string[] | undefined;
  };
  PK: string;
  SK: string;
}

interface MemberOverview {
  membership?: {
    segments?: {
      in: Array<{ [key: string]: any }>;
    };
  };
}

interface SegmentsSectionProps {
  segmentsData: Segment[];
  memberData?: MemberOverview;
}

const SegmentsSection: React.FC<SegmentsSectionProps> = ({ segmentsData, memberData }) => {
  const { t } = useTranslation();
  const singleSendsEnabled = useSingleSendsEnabled();
  const [searchTerm, setSearchTerm] = useState('');
  const [membershipFilter, setMembershipFilter] = useState<'all' | 'member' | 'notMember'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'peopleCount' | 'lastModifiedAt'>('name');

  // Get member's segment IDs
  const memberSegmentIds = useMemo(() => {
    const segmentValues = memberData?.membership?.segments?.in.map((segment) => Object.values(segment)).flat() || [];
    return new Set(segmentValues);
  }, [memberData]);

  // Add membership status to segments
  const segmentsWithMembership = useMemo(() => {
    return segmentsData.map((segment) => ({
      ...segment,
      isMember: memberSegmentIds.has(segment.id),
    }));
  }, [segmentsData, memberSegmentIds]);

  // Filter and sort segments
  const filteredSegments = segmentsWithMembership
    .filter((segment) => {
      const matchesSearch =
        segment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        segment.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesMembership =
        membershipFilter === 'all' ||
        (membershipFilter === 'member' && segment.isMember) ||
        (membershipFilter === 'notMember' && !segment.isMember);

      return matchesSearch && matchesMembership;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'peopleCount':
          return parseInt(b.peopleCount || '0') - parseInt(a.peopleCount || '0');
        case 'lastModifiedAt':
          return new Date(b.lastModifiedAt).getTime() - new Date(a.lastModifiedAt).getTime();
        default:
          return a.name.localeCompare(b.name);
      }
    });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatNumber = (numString: string) => {
    const num = parseInt(numString || '0');
    return new Intl.NumberFormat().format(num);
  };

  const getUsageCount = (segment: Segment) => {
    const usage = segment.usage;
    let totalUsage = 0;

    if (singleSendsEnabled && usage.singleSends?.length) totalUsage += usage.singleSends.length;
    if (usage.campaigns?.length) totalUsage += usage.campaigns.length;
    if (usage.dashboard?.length) totalUsage += usage.dashboard.length;
    if (usage.sqlSyncs?.length) totalUsage += usage.sqlSyncs.length;
    if (usage.apiTriggered?.length) totalUsage += usage.apiTriggered.length;

    return totalUsage;
  };

  const memberCount = segmentsWithMembership.filter((s) => s.isMember).length;
  const notMemberCount = segmentsWithMembership.filter((s) => !s.isMember).length;

  return (
    <CustomCard title="User Groups">
      {/* Header */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-custom-darkSilver">
            <span>
              {memberCount} Member • {notMemberCount} Not Member • {segmentsData.length} Total
            </span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-2 mb-8">
          {/* Search */}
          <SearchField initialValue={searchTerm} setSearchValue={setSearchTerm} withFilter={false} />

          {/* Filter */}
          <select
            value={membershipFilter}
            onChange={(e) => setMembershipFilter(e.target.value as any)}
            className="w-[150px] bg-custom-silver w-fit rounded-lg text-custom-black text-sm px-2"
          >
            <option value="all">All Segments</option>
            <option value="member">Member Of</option>
            <option value="notMember">Not Member Of</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-auto">
        {filteredSegments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-custom-darkSilver font-medium">No segments found</div>
            <div className="text-custom-darkSilver text-sm">
              {searchTerm || membershipFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'No segments have been created yet'}
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-custom-silver overflow-hidden">
            <table className="w-full">
              <thead className="bg-custom-silver">
                <tr>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">Status</th>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">Type</th>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">Name</th>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">People Count</th>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">Usage</th>
                  <th className="px-4 py-2 text-left text-sm text-custom-darkSilver font-medium">Last Modified</th>
                </tr>
              </thead>
              <tbody>
                {filteredSegments.map((segment) => {
                  const usageCount = getUsageCount(segment);

                  return (
                    <tr key={segment.id} className="group text-sm h-10 border-b border-custom-silver last:border-b-0">
                      {/* Status */}
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        <div className="flex items-center">
                          <div
                            className={`w-2 h-2 rounded-full mr-2 ${
                              segment.isMember ? 'bg-custom-green' : 'bg-custom-darkSilver'
                            }`}
                          ></div>
                          <span
                            className={`text-sm ${segment.isMember ? 'text-custom-black' : 'text-custom-darkSilver'}`}
                          >
                            {segment.isMember ? 'Member' : 'Not Member'}
                          </span>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        <div className="flex items-center">
                          <div
                            className={`w-4 h-4 mr-1 ${
                              segment.segmentType === 'manual' ? 'text-custom-blue' : 'text-custom-yellow'
                            }`}
                          >
                            {segment.segmentType === 'manual' ? (
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                              </svg>
                            ) : (
                              <svg fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                              </svg>
                            )}
                          </div>
                          <span className="text-sm text-custom-darkSilver capitalize">
                            {segment.segmentType || 'Data-driven'}
                          </span>
                        </div>
                      </td>

                      {/* Name */}
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        <div className="text-custom-darkSilver">
                          <div className="font-semibold">{segment.name}</div>
                          <div className="text-xs mt-1">Created on {formatDate(segment.createdAt)}</div>
                        </div>
                      </td>

                      {/* People Count */}
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 text-custom-darkSilver mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                          </svg>
                          <span className="text-custom-darkSilver">{formatNumber(segment.peopleCount)}</span>
                        </div>
                      </td>

                      {/* Usage */}
                      <td className="px-4 py-2 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        <div className="space-y-0.5">
                          {singleSendsEnabled && (
                            <div className="flex items-center">
                              <span className="bg-custom-yellow px-1.5 py-0.5 rounded-full text-white text-xs text-center font-semibold mr-1">
                                {segment.usage.singleSends?.length || 0}
                              </span>
                              <span className="text-custom-darkSilver text-sm">Single sends</span>
                            </div>
                          )}
                          <div className="flex items-center">
                            <span className="bg-custom-darkestBlue px-1.5 py-0.5 rounded-full text-white text-xs text-center font-semibold mr-1">
                              {segment.usage.campaigns?.length || 0}
                            </span>
                            <span className="text-custom-darkSilver text-sm">Campaigns</span>
                          </div>
                        </div>
                      </td>

                      {/* Last Modified */}
                      <td className="px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none text-custom-darkSilver bg-white">
                        <span>{formatDate(segment.lastModifiedAt)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </CustomCard>
  );
};

export default SegmentsSection;
